import { CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { KEYBOARD_KEYS } from '@/shared/lib/constants';

import styles from './Chip.module.css';

type ChipProps = {
  /** Icon to display before the label */
  icon?: React.ReactNode;
  /** Text content of the chip */
  label: React.ReactNode;
  /** Whether the chip is disabled */
  disabled?: boolean;
  /** Callback when close button is clicked */
  onClose?: () => void;
  /** Callback when chip is clicked */
  onClick?: () => void;
  /** Additional CSS classes to apply to the chip */
  className?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick' | 'role' | 'tabIndex' | 'className'>;

const Chip = ({ icon, label, disabled = false, onClose, onClick, className, ...props }: ChipProps) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (disabled) return;

    if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.SPACE) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClose) return;
    e.stopPropagation();
    onClose();
  };

  return (
    <div
      className={classNames(styles.chip, className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      data-button={!!onClick}
      data-disabled={disabled}
      tabIndex={onClick && !disabled ? 0 : -1}
      {...props}>
      {icon}
      <span className={styles.label}>{label}</span>
      {!disabled && onClose && (
        <button className={styles.closeButton} onClick={handleClose} type="button">
          <CloseOutlined className={styles.closeIcon} />
        </button>
      )}
    </div>
  );
};

export { Chip };
export type { ChipProps };
